import React, { useEffect, useRef, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./SessionTimeout.css";
import {
  subscribeToEvent,
  unsubscribeFromEvent,
} from "../../MasterData/emitter";

const SessionTimeoutProvider = ({ children }) => {
  const navigate = useNavigate();
  const countdownRef = useRef(null);

  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const [timeoutMessage, setTimeoutMessage] = useState(
    "Session timeout. Please login again."
  );

  const WARNING_COUNTDOWN = 10; // seconds before auto-logout once API says session expired

  const clearSessionData = useCallback(() => {
    localStorage.clear();
    sessionStorage.clear();

    document.cookie.split(";").forEach((c) => {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });
  }, []);

  const performLogout = useCallback(() => {
    clearSessionData();
    setShowWarning(false);

    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }

    navigate("/", { replace: true });
  }, [navigate, clearSessionData]);

  const startCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    setCountdown(WARNING_COUNTDOWN);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          performLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [performLogout]);

  useEffect(() => {
    const handleSessionTimeout = (payload) => {
      setTimeoutMessage(
        payload?.uiMsg || payload?.message || "Session timeout. Please login again."
      );
      setShowWarning(true);
      startCountdown();
    };

    subscribeToEvent("session-timeout", handleSessionTimeout);

    return () => {
      unsubscribeFromEvent("session-timeout", handleSessionTimeout);
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [startCountdown]);

  const progressPercentage =
    ((WARNING_COUNTDOWN - countdown) / WARNING_COUNTDOWN) * 100;

  return (
    <>
      {children}

      {showWarning && (
        <div className="session-timeout-overlay">
          <div className="session-timeout-modal">
            <div className="floating-circles">
              <div className="circle circle-1"></div>
              <div className="circle circle-2"></div>
              <div className="circle circle-3"></div>
            </div>

            <div className="warning-icon-container">
              <div className="warning-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>

            <div className="modal-content">
              <h2 className="modal-title">Session Timeout</h2>
              <p className="modal-description">{timeoutMessage}</p>

              <div className="countdown-container">
                <div className="countdown-circle">
                  <svg className="countdown-svg" viewBox="0 0 120 120">
                    <circle
                      className="countdown-bg"
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      stroke="rgba(255,255,255,0.1)"
                      strokeWidth="8"
                    />
                    <circle
                      className="countdown-progress"
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      stroke="url(#gradient)"
                      strokeWidth="8"
                      strokeLinecap="round"
                      style={{
                        strokeDasharray: `${2 * Math.PI * 50}`,
                        strokeDashoffset: `${
                          2 * Math.PI * 50 * (1 - progressPercentage / 100)
                        }`,
                        transform: "rotate(-90deg)",
                        transformOrigin: "60px 60px",
                      }}
                    />
                    <defs>
                      <linearGradient
                        id="gradient"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="0%"
                      >
                        <stop offset="0%" stopColor="#ff6b6b" />
                        <stop offset="100%" stopColor="#ffa726" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="countdown-number">
                    <span className="countdown-text">{countdown}</span>
                    <span className="countdown-label">sec</span>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  className="btn btn-logout"
                  onClick={performLogout}
                  type="button"
                >
                  <span className="btn-icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9M16 17L21 12M21 12L16 7M21 12H9"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  Login again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SessionTimeoutProvider;
